import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site-shell";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Policy — DealDraft" },
      {
        name: "description",
        content: "How DealDraft collects, uses, and protects your data.",
      },
    ],
    links: [{ rel: "canonical", href: "https://dealdraft.pro/privacy" }],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <SiteShell>
      <article className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Privacy Policy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Last updated: August 10, 2026</p>

        <div className="mt-8 space-y-10 text-base leading-relaxed text-muted-foreground">
          <p>
            DealDraft (dealdraft.pro) is operated by <strong className="text-foreground">LJ
            Legacy Solutions LLC</strong>, a Kansas limited liability company. LJ Legacy
            Solutions LLC is the data controller for the information described in this
            policy. If you have questions about this policy or your data, contact us at{" "}
            <a
              href="mailto:support@dealdraft.pro"
              className="font-medium text-foreground underline hover:no-underline"
            >
              support@dealdraft.pro
            </a>
            .
          </p>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Information We Collect
            </h2>
            <ul className="mt-4 list-disc space-y-2 pl-5">
              <li>
                <strong className="text-foreground">Account information.</strong> Your email
                address, collected and managed through Supabase Auth when you create an
                account.
              </li>
              <li>
                <strong className="text-foreground">Content you generate.</strong> The
                proposal and contract content you create, along with your document history,
                stored in our Supabase database.
              </li>
              <li>
                <strong className="text-foreground">Payment information.</strong> Payments
                are handled by Stripe. We do not store your card numbers — Stripe processes
                and stores your payment data as our payment processor.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Subprocessors
            </h2>
            <p className="mt-4">
              We share data with a small number of subprocessors, each used for a specific
              purpose:
            </p>
            <ul className="mt-4 list-disc space-y-2 pl-5">
              <li>
                <strong className="text-foreground">Supabase</strong> — authentication and
                data storage.
              </li>
              <li>
                <strong className="text-foreground">Stripe</strong> — payment processing.
              </li>
              <li>
                <strong className="text-foreground">Google (Google Analytics 4)</strong> —
                aggregate, anonymized usage analytics, loaded only after you consent.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">Cookies</h2>
            <div className="mt-4 space-y-4">
              <div>
                <h3 className="font-medium text-foreground">Strictly Necessary Cookies</h3>
                <p className="mt-2">
                  We use only strictly-necessary cookies required for the service to
                  function: Supabase authentication/session cookies, and Stripe
                  payment/session cookies during checkout.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-foreground">Analytics Cookies</h3>
                <p className="mt-2">
                  We use Google Analytics 4 (GA4) to measure aggregate site usage, such as
                  which pages are visited. GA4's script and cookies only load after you click
                  "Accept" on our cookie banner. If you click "Decline," or take no action,
                  GA4 does not load and you are not tracked. You can change your choice at any
                  time by clearing your browser's local storage for dealdraft.pro.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Data Retention
            </h2>
            <p className="mt-4">
              We retain your account and document data for as long as your account remains
              active. You may request deletion of your account and associated data at any
              time by emailing{" "}
              <a
                href="mailto:support@dealdraft.pro"
                className="font-medium text-foreground underline hover:no-underline"
              >
                support@dealdraft.pro
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Your Rights
            </h2>
            <p className="mt-4">
              Depending on where you live, you may have rights to access the personal data we
              hold about you, request corrections, request deletion, or object to certain
              uses of your data. To exercise any of these rights, contact us at{" "}
              <a
                href="mailto:support@dealdraft.pro"
                className="font-medium text-foreground underline hover:no-underline"
              >
                support@dealdraft.pro
              </a>{" "}
              and we will respond as required by applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Data Security
            </h2>
            <p className="mt-4">
              We rely on our subprocessors' industry-standard security practices, including
              encryption in transit, to help protect your information. No method of
              transmission or storage is completely secure, but we take reasonable steps to
              safeguard your data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Children's Privacy
            </h2>
            <p className="mt-4">
              DealDraft is not directed to individuals under the age of 18, and we do not
              knowingly collect personal information from children.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              International Users
            </h2>
            <p className="mt-4">
              DealDraft is operated from the United States. If you access the service from
              outside the United States, your information will be processed and stored in
              the United States by us and our subprocessors.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold tracking-tight text-foreground">
              Changes to This Policy
            </h2>
            <p className="mt-4">
              We may update this Privacy Policy from time to time. If we make material
              changes, we will update the "Last updated" date at the top of this page.
            </p>
          </section>
        </div>
      </article>
    </SiteShell>
  );
}
